import {useFormik} from 'formik'
import {useCallback, useMemo} from 'react'
import {TextInput} from '../../../../components/inputs'
import {MetronicIconButton} from '../../../../components/inputs/MetronicIconButton'
import {SweetAlert} from '../../../../components/modals/SweetAlert'
import * as Yup from 'yup'
import {VoucherModel} from '../../../../models/svc/VoucherModel'
import {useAlerts} from '../../../../components/alerts/useAlerts'
import {HorizontalDivider} from '../../../../components/utils/HorizontalDivider'
interface EmailModalProps {
  open: boolean
  setOpen: (value: boolean) => void
  data: VoucherModel[]
  onSubmit: (values: InitialValuesProps) => void
}

export interface InitialValuesProps {
  email: string
  name: string
}

export const EmailVoucherModal = ({open, setOpen, data, onSubmit}: EmailModalProps) => {
  const {pushError} = useAlerts()
  const initialValues = useMemo<InitialValuesProps>(() => {
    return {
      email: '',
      name: '',
    }
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: EmailSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      try {
        await onSubmit(values)
      } catch (err) {
        pushError(err)
      } finally {
        setSubmitting(false)
        setOpen(false)
      }
    },
  })

  const handleClose = useCallback(() => {
    setOpen(false)
    formik.resetForm()
  }, [formik, setOpen])

  const title = useMemo(() => {
    if (data.length > 1) return 'EMAIL SELECTED VOUCHERS'
    return `EMAIL VOUCHER: ${data[0].name}`
  }, [data])

  return (
    <SweetAlert
      open={open}
      onClose={handleClose}
      showConfirmButton={false}
      containerClassName='mw-300px'
      customClass={{
        popup: 'bg-primary-cp-dark',
      }}
    >
      <div className='d-flex'>
        <div className='flex-grow-1 text-start d-flex flex-column justify-content-center'>
          <p className='text-white mb-0 fw-bolder'>{title}</p>
        </div>
        <div>
          <MetronicIconButton
            type='button'
            className='btn'
            iconType='Navigation'
            iconName='Close'
            variant='text'
            size='md'
            color='white'
            onClick={handleClose}
          />
        </div>
      </div>
      <HorizontalDivider className='bg-dark' />
      <form
        className='form mt-5'
        onSubmit={formik.handleSubmit}
        style={{
          minWidth: '300px',
        }}
      >
        <TextInput
          type='name'
          placeholder='Enter Name'
          inputClassName='rounded-0'
          {...formik.getFieldProps('name')}
          errorMessage={formik.errors.name}
          isTouched={formik.touched.name}
        />
        <TextInput
          type='email'
          placeholder='Enter Email Address'
          inputClassName='rounded-0'
          {...formik.getFieldProps('email')}
          errorMessage={formik.errors.email}
          isTouched={formik.touched.email}
        />

        <button
          disabled={formik.isSubmitting}
          className='btn bg-body d-flex gap-2 m-auto mt-5 rounded-0 text-white w-50 justify-content-center'
          onClick={formik.submitForm}
        >
          Send Email
          {formik.isSubmitting && (
            <span className='indicator-progress ml-2' style={{display: 'block'}}>
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </form>
    </SweetAlert>
  )
}

const EmailSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  name: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Name is required'),
})
